"use client";
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams, ReadonlyURLSearchParams, usePathname } from "next/navigation";
import { toLocalISOString } from "@/lib/formatHour";
import { track } from "@/lib/tracking";

type DateRange = {
  startDate: Date;
  endDate: Date;
};

type TimeSlots = {
  startTime: number;
  endTime: number;
};

type DateRangeContextType = {
  range: DateRange;
  setRange: (range: DateRange) => void;
  timeSlots: TimeSlots;
  setTimeSlots: (slots: TimeSlots) => void;
  disabledDates: Date[];
  handleFromDateSelect: (date: Date) => void;
  handleToDateSelect: (date: Date) => void;
  handleTimeSelect: (time: number, isStart: boolean) => void;
  handleClearAll: () => void;
  savePickDropTime: (pickupMinute?: number, dropOffMinute?: number) => void;
  SetDate: (pathname: string, params: ReadonlyURLSearchParams, from: string, to: string) => string;
  isInitialized: boolean;
};

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export const DateRangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasInitializedFromURL = useRef(false);


  // Mark as hydrated after first render
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Initialize range: if it's 11 PM or later, default to tomorrow → day after; else today → tomorrow
  const [range, setRange] = useState<DateRange>(() => {
    const n = new Date();
    if (n.getHours() >= 23) {
      const tomorrow = new Date(n);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dayAfter = new Date(tomorrow);
      dayAfter.setDate(dayAfter.getDate() + 1);
      return { startDate: tomorrow, endDate: dayAfter };
    }
    const tomorrow = new Date(n);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return { startDate: n, endDate: tomorrow };
  });
  const [timeSlots, setTimeSlots] = useState<TimeSlots>(() => {
    const n = new Date();
    if (n.getHours() >= 23) return { startTime: 9, endTime: 18 };
    return {
      startTime: n.getHours(),
      endTime: Math.min(n.getHours(), 23),
    };
  });

  // Memoized disabledDates (past 365 days, excluding today)
  const disabledDates = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 365 }, (_, i) => {
      const date = new Date();
      date.setDate(today.getDate() - (i + 1)); // Start from yesterday (i+1) to exclude today
      return date;
    });
  }, []);

  // Helper to parse local date/time string
  function parseLocalDateTime(str: string) {
    try {
      // Handle format like "2025-08-19T08:00:00.000Z" or "2025-08-19T08:00:00"
      const [datePart, timePart] = str.split('T');
      if (!datePart || !timePart) {
        console.error('Invalid date format:', str);
        return new Date();
      }
      
      const [year, month, day] = datePart.split('-').map(Number);
      // Remove milliseconds and timezone info if present
      const cleanTimePart = timePart.split('.')[0];
      const [hour, minute, second] = cleanTimePart.split(':').map(Number);
      
      return new Date(year, month - 1, day, hour, minute, second);
    } catch (error) {
      console.error('Error parsing date:', str, error);
      return new Date();
    }
  }

  // Read URL parameters on mount and when searchParams change (for page refresh)
  useEffect(() => {
    if (!isHydrated) return; // Don't run until hydrated
    
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    
    if (from && to) {
      try {
        const fromDate = parseLocalDateTime(from);
        const toDate = parseLocalDateTime(to);
        
        setRange({ startDate: fromDate, endDate: toDate });
        setTimeSlots({
          startTime: fromDate.getHours(),
          endTime: toDate.getHours(),
        });
        
        hasInitializedFromURL.current = true;
        setIsInitialized(true);
      } catch (error) {
        console.error("🔄 DateRangeContext: Error parsing URL dates:", error);
        setIsInitialized(true);
      }
    } else {
      setIsInitialized(true);
    }
  }, [isHydrated, searchParams]); // Include searchParams to detect page refresh

  // Simple function to update URL with current date/time values
  const updateURL = useCallback((newRange?: DateRange, newTimeSlots?: TimeSlots) => {
    if (!isHydrated) return;
    
    // Use provided values or current state
    const currentRange = newRange || range;
    const currentTimeSlots = newTimeSlots || timeSlots;
    
    const pickupDate = new Date(currentRange.startDate);
    pickupDate.setHours(currentTimeSlots.startTime, 0, 0, 0);
    const dropOffDate = new Date(currentRange.endDate);
    dropOffDate.setHours(currentTimeSlots.endTime, 0, 0, 0);
    
    const newFrom = toLocalISOString(pickupDate);
    const newTo = toLocalISOString(dropOffDate);
    
    const params = new URLSearchParams(searchParams);
    params.set("from", newFrom);
    params.set("to", newTo);
    const newUrl = `${pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  }, [isHydrated, pathname, searchParams, range, timeSlots]);

  // Handlers
  const handleFromDateSelect = useCallback((date: Date) => {
    let newEndDate = range.endDate;
    if (date > newEndDate) {
      newEndDate = new Date(date);
      newEndDate.setDate(newEndDate.getDate() + 1);
    }
    const newRange = { startDate: date, endDate: newEndDate };
    setRange(newRange);
    updateURL(newRange, timeSlots);

    track('date_selected', {
      date_type: 'from',
      from_date: toLocalISOString(date),
    });
  }, [range, timeSlots, updateURL]);

  const handleToDateSelect = useCallback((date: Date) => {
    let newRange;
    if (date < range.startDate) {
      newRange = { startDate: range.startDate, endDate: range.startDate };
    } else {
      newRange = { startDate: range.startDate, endDate: date };
    }
    setRange(newRange);
    updateURL(newRange, timeSlots);

    const durationDays = Math.max(1, Math.round(
      (newRange.endDate.getTime() - newRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
    ));
    track('date_selected', {
      date_type: 'to',
      to_date: toLocalISOString(date),
      duration_days: durationDays,
    });
  }, [range, timeSlots, updateURL]);

  const handleTimeSelect = useCallback((time: number, isStart: boolean) => {
    const newTimeSlots = {
      ...timeSlots,
      [isStart ? "startTime" : "endTime"]: time,
    };
    setTimeSlots(newTimeSlots);
    // Update URL immediately with new values
    updateURL(range, newTimeSlots);
  }, [timeSlots, range, updateURL]);

  const handleClearAll = useCallback(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(tomorrow);
    dayAfter.setDate(dayAfter.getDate() + 1);

    const newRange =
      now.getHours() >= 23
        ? { startDate: tomorrow, endDate: dayAfter }
        : { startDate: now, endDate: tomorrow };
    const newTimeSlots = {
      startTime: 9,
      endTime: 18,
    };

    setRange(newRange);
    setTimeSlots(newTimeSlots);
    updateURL(newRange, newTimeSlots);
  }, [updateURL]);

  const SetDate = useCallback((
    pathname: string,
    params: ReadonlyURLSearchParams,
    from: string,
    to: string
  ) => {
    const newParams = new URLSearchParams(params);
    newParams.set("from", from);
    newParams.set("to", to);
    return `${pathname}?${newParams.toString()}`;
  }, []);

  // Save pick/drop time to storage when called
  const savePickDropTime = useCallback((pickupMinute = 0, dropOffMinute = 0) => {
    const pickupDate = new Date(range.startDate);
    pickupDate.setHours(timeSlots.startTime, pickupMinute, 0, 0);
    const dropOffDate = new Date(range.endDate);
    dropOffDate.setHours(timeSlots.endTime, dropOffMinute, 0, 0);
    // storage.setPickDropTime({
    //   pickup: pickupDate.toISOString(),
    //   dropOff: dropOffDate.toISOString(),
    // });
  }, [range, timeSlots]);

  return (
    <DateRangeContext.Provider
      value={{
        range,
        setRange,
        timeSlots,
        setTimeSlots,
        disabledDates,
        handleFromDateSelect,
        handleToDateSelect,
        handleTimeSelect,
        handleClearAll,
        savePickDropTime,
        SetDate,
        isInitialized,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};

export function useDateRange() {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error("useDateRange must be used within a DateRangeProvider");
  }
  return context;
} 